// Enhanced User type matching API specification
export interface User {
  id: number; // Changed from string based on API response
  email: string;
  full_name: string; // Added
  avatar_url?: string | null; // Profile picture URL (populated by /auth/google, /auth/login, /auth/me)
  is_active: boolean; // Added
  is_verified: boolean; // Added
  is_superuser?: boolean; // Returned by /auth/google and newer /auth/login responses
  created_at: string;
  updated_at?: string; // Optional in some responses
}

// Payload for POST /auth/google — the GIS id_token (aka credential).
export interface GoogleLoginRequest {
  id_token: string;
}

// Enhanced Authentication Response Types
export interface AuthResponse {
  access_token: string;
  token_type: string;
  user?: User; // User data might not be included in login response
}

export interface RegisterResponse {
  user: User;
  verification_token: string;
  expires_in: number;
  message: string;
  // Optional access token for seamless flow (some backends provide this)
  access_token?: string;
  token_type?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: User;
  requires_verification: boolean;
  verification_token?: string; // Only for unverified users
  verification_expires_in?: number; // Only for unverified users
  message?: string; // Only for unverified users
}

// Request Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string; // Added
}

export interface VerifyEmailRequest {
  code: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface ResetPasswordRequest {
  code: string;
  new_password: string;
}

export interface UpdateProfileRequest {
  full_name: string;
}

export interface UpdatePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface UpdateEmailRequest {
  new_email: string;
  password: string;
}

// Response Types
export interface VerificationResponse {
  message: string;
  success: boolean;
}

export interface PasswordResetRequestResponse {
  message: string;
  reset_token: string;
  expires_in: number;
}

export interface VerificationTokenResponse {
  verification_token: string;
  expires_in: number;
  message: string;
  user: User;
}

export interface EmailUpdateResponse {
  message: string;
  verification_token: string;
  expires_in: number;
}

export interface UserStats {
  user_id: number;
  email: string;
  full_name: string;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  active_tokens: number;
}

// Validation Error Types (422 responses)
export interface ValidationError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input: any;
  ctx?: Record<string, any>;
}

export interface ValidationErrorResponse {
  detail: ValidationError[];
}

// Upload and File types
export interface UploadedFile {
  /** Normalized in api.uploadFile() from `file_record_id` (the real /upload/
   *  response field) — the raw response has no `id`. */
  id: number;
  /** The actual id field the /upload/ response returns. */
  file_record_id?: number;
  filename: string;
  content_type: string;
  file_type: string;
  file_size: number;
  created_at: string;
  updated_at: string;
  ai_description?: string;
  storage_url: string;
  text_content?: string;
  user_id: number;
  vector_collection: string;
}

export interface InventoryItem {
  id: number;
  item_name: string;
  item_description?: string;
  item_category?: string;
  location?: string;
  tags?: string;
  confidence_score: number;
  created_at: string;
  updated_at: string;
  file_record_id: number;
  user_id: number;
  brand?: string;
  condition?: string;
  estimated_value?: number;
  is_verified: boolean;
  notes?: string;
}

// Enhanced inventory item with sharing information (from /users/inventory API)
export interface EnhancedInventoryItem {
  id: number;
  item_name: string;
  item_description: string;
  item_category: string;
  brand: string | null;
  estimated_value: number;
  condition: string;
  location: string;
  tags: string;
  confidence_score: number;
  is_verified: boolean;
  notes: string | null;
  perishability_type: string;
  estimated_expiry_date: string | null;
  expiry_confidence_score: number;
  prominence_score: number;
  visual_area_percentage: number;
  ownership_likelihood: number;
  exclusion_flags: string[];
  requires_user_verification: boolean;
  source_file_id: number;
  source_image_url: string;
  source_url_expires_at: string | null;
  is_public: boolean;
  is_shareable: boolean;
  sharing_info: SharingInfo;

  /** Polished cutout produced by the post-confirm primary-photo
   *  pipeline (crop + AuraSR upscale + mask re-apply). Optional —
   *  not every item has one yet, and the field may be absent on
   *  legacy rows. Render this in preference to source_image_url
   *  when present. */
  primary_photo_url?: string | null;
  primary_photo_source?: 'crop_upscale' | 'crop_only' | 'original' | null;

  // ── Custody (Sprint 3 / BE-210) — optional on this response shape ──
  /** Original owner of the item. Differs from `current_custodian_user_id`
   *  when the item has been lent/handed off. Optional because legacy
   *  rows don't include it; selectors must tolerate undefined. */
  user_id?: number | null;
  /** Whoever currently physically holds the item. Equals `user_id`
   *  pre-handoff. Required by the "Just held" filter on the Items
   *  page (`held_by_me=true` widens the result; this field
   *  disambiguates owner-vs-custodian client-side). */
  current_custodian_user_id?: number | null;
  /** Custody-since timestamp (ISO). Null pre-handoff. */
  custody_since?: string | null;
  /** Optional return-by date set at handoff time. */
  custody_due_date?: string | null;
}

export interface SharingInfo {
  is_owned: boolean;
  is_borrowed: boolean;
  is_shared: boolean;
  sharing_status: string | null;
  shared_with_name: string | null;
  shared_with_email: string | null;
  share_url: string | null;
  sharing_type: string | null;
  shared_at: string | null;
  claimed_at: string | null;
  returned_at: string | null;
  expected_return_date: string | null;
  sharing_notes: string | null;
  borrowed_from_name: string | null;
  borrowed_from_email: string | null;
  borrowed_at: string | null;
  borrow_message: string | null;
  borrow_expected_return: string | null;
  borrow_status: string | null;
  can_send_reminder: boolean;
  last_reminder_sent: string | null;
  reminder_count: number;
  sharing_history: any | null;
}

// Enhanced inventory API request parameters
export interface EnhancedInventoryRequest {
  // Pagination
  limit?: number;
  offset?: number;
  
  // Backward Compatible Filters
  category?: string;
  verified_only?: boolean;
  
  // New Sharing Status Filters
  is_public?: boolean;
  is_shareable?: boolean;
  sharing_status?: 'owned' | 'shared' | 'borrowed';
  
  // Search & Text Filters
  search?: string;
  brand?: string;
  condition?: string;
  location?: string;
  
  // Value Range Filters
  min_value?: number;
  max_value?: number;
  min_confidence?: number;
  
  // Content & Analysis Filters
  has_image?: boolean;
  perishability_type?: string;
  requires_verification?: boolean;
  
  // Sorting Options
  sort_by?: 'created_at' | 'updated_at' | 'item_name' | 'estimated_value' | 'confidence_score';
  sort_order?: 'asc' | 'desc';

  // FE-221 / BE-210 — widen the response to include items where the
  // caller is the current_custodian_user_id (in-crew handoffs they've
  // received from owners). Default omitted = legacy owned-only contract.
  // DEPRECATED by the backend (issue #324) in favor of `scope`; `scope`
  // wins when both are sent.
  held_by_me?: boolean;

  // issue #324 — server-side scope partition that replaces the FE's
  // client-side pill `.filter()` over a paginated cache (#167). One
  // paginated request returns exactly the rows for the pill:
  //   in_my_possession → "Mine"   ·  lent_out → "Lent out"
  //   borrowed         → "Borrowed" ·  all → owned ∪ held
  scope?: 'all' | 'in_my_possession' | 'lent_out' | 'borrowed';
}

// Enhanced inventory API response
export interface EnhancedInventoryResponse {
  inventory: EnhancedInventoryItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    total_pages: number;
    current_page: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

// API Response wrappers
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
  has_next?: boolean;
}

// Backend-specific response types (what the backend actually returns)
export interface FilesResponse {
  files: UploadedFile[];
  total: number;
  offset: number;
  limit: number;
}

export interface InventoryResponse {
  inventory: InventoryItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface ApiError {
  detail: string;
  status_code: number;
}

// Health check
export interface HealthResponse {
  status: string;
  version: string;
  timestamp: string;
}

// Feedback System Types
export type FeedbackType = 
  | 'bug_report'
  | 'feature_request' 
  | 'general_feedback'
  | 'user_experience'
  | 'performance_issue'
  | 'suggestion'
  | 'complaint'
  | 'compliment';

export interface FeedbackFormData {
  // Required fields
  feedback_type: FeedbackType;
  feedback_title: string;
  feedback_content: string;
  
  // Optional user fields (auto-filled if authenticated)
  user_email?: string;
  user_name?: string;
  
  // Optional context fields (auto-captured)
  page_url?: string;
  page_title?: string;
  referrer_url?: string;
  user_agent?: string;
  screen_resolution?: string;
  browser_language?: string;
  viewport_size?: string;
  device_type?: string;
  operating_system?: string;
  session_id?: string;
  
  // Optional preference fields
  rating?: number; // 0-5
  would_recommend?: boolean;
  follow_up_requested?: boolean;
}

export interface FeedbackSubmissionRequest extends FeedbackFormData {
  screenshot?: File;
}

export interface FeedbackSubmissionResponse {
  success: boolean;
  message: string;
  feedback_id: number;
  screenshot_upload_url?: string;
}

export interface FeedbackTypeOption {
  value: string;
  label: string;
  description: string;
}

export interface FeedbackTypesResponse {
  feedback_types: FeedbackTypeOption[];
}

// Enhanced User Statistics (New API Structure)
export interface NetWorthStats {
  total_estimated_value: number;
  verified_value: number;
  unverified_value: number;
  monthly_growth: number;
  top_category_by_value?: string;
  top_category_value: number;
}

export interface InventorySummaryStats {
  total_items: number;
  verified_items: number;
  unverified_items: number;
  completion_percentage: number;   // 0-100
  categories_count: number;
  unique_brands: number;
  items_this_month: number;
  total_files: number;
}

export interface AchievementStats {
  upload_streak_days: number;
  completion_score: number;        // 0-1000 scale
  rank: string;                   // User rank title
  next_milestone: string;         // Description of next achievement
  badges_earned: string[];        // Array of badge names
  progress_to_next_rank: number;  // 0-100 percentage
}

export interface ActivityStats {
  items_added_last_7_days: number;
  items_added_last_30_days: number;
  photos_uploaded: number;
  most_active_day: string;        // Day of week
  upload_frequency: string;       // Human-readable description
  last_upload_date?: string;      // ISO datetime
}

export interface UserStatisticsResponse {
  net_worth: NetWorthStats;
  inventory_summary: InventorySummaryStats;
  achievements: AchievementStats;
  activity: ActivityStats;
  last_updated: string;         // ISO datetime
  cached: boolean;              // Whether data came from cache
  calculation_time_ms: number;  // Performance metric
}

// Enhanced File Management Types
/** FE #198 / BE #286 — active recall context. Surfaced on
 *  InventoryItemResponse.active_recall when the item has a PENDING
 *  RECALL borrow request. Owner + current custodian see the full
 *  blob.
 *
 *  Shipped BE shape (verified on prod 2026-05-26):
 *  - request_id        — the borrow_requests row id (use for ack/cancel)
 *  - requester_id      — who issued the recall (no denormalized name yet)
 *  - message           — the recall reason text
 *  - created_at        — used for the "1m ago" relative timestamp
 *  - expires_at        — backend-computed deadline (typically +7d)
 *
 *  Original FE-198 spec also expected `requester_user_id` /
 *  `requester_name` / `reason` / `expected_by` fields. We accept BOTH
 *  shapes so we're forward-compatible if the BE adds them later, and
 *  the FE consumer-side resolution helpers (see `recallReason` etc.
 *  on `RecallBanner`) read whichever is present.
 */
export interface ActiveRecallInfo {
  request_id: number;
  // — Actual BE-shipped fields (preferred reads).
  requester_id?: number;
  message?: string;
  expires_at?: string;
  // — Original spec fields (still accepted, fall through to the BE
  //   shape when missing). Removed once BE aligns.
  requester_user_id?: number;
  requester_name?: string;
  reason?: string;
  expected_by?: string | null;
  created_at: string;
}

/** Tiny crew summary returned in `InventoryItemResponse.groups[]`.
 *  Backend pre-joins minimal metadata so the FE can render group
 *  chips on item cards without a separate /groups lookup. */
export interface GroupSummaryForItem {
  id: number;
  name: string;
  kind: string; // 'CREW' | 'FAMILY' | 'TEAM' | 'PERSONAL'
}

// ========================================================================
// SPRINT 5 / CHUNK 11.5 — Quick-add + Voice metadata extraction
// Reference: BE-11.5.1 (POST /inventory/quick-add) +
//            BE-11.5.2 (POST /inventory/{id}/extract-metadata).
// Both endpoints live on prod.
// ========================================================================

/** Body for `POST /inventory/quick-add`. The user uploads a photo
 *  via `/upload/` first to get a `file_record_id`, then fires this
 *  with the metadata fields. Endpoint creates the inventory item
 *  + binds to the listed crew ids in one call. */
export interface QuickAddRequest {
  file_record_id: number;
  item_name: string;
  item_description?: string | null;
  item_category?: string | null;
  brand?: string | null;
  estimated_value?: number | null;
  quantity?: number;
  condition?: string | null;
  location?: string | null;
  // FE-214 — serial_number dropped on BE-202.
  notes?: string | null;
  /** Optional crew bindings. Backend filters to crews the caller is
   *  ACTIVE in; unknown ids silently skipped. */
  group_ids?: number[];
}

export interface QuickAddResponse {
  item_id: number;
  file_record_id: number;
  item_name: string;
  bound_to_group_ids: number[];
  created_at: string;
}

/** Body for `POST /inventory/{id}/extract-metadata`. */
export interface ExtractMetadataRequest {
  transcript: string;
}

/** Structured fields extracted by the server-side LLM. */
export interface ExtractedMetadata {
  purchase_date?: string | null;
  estimated_value?: number | null;
  warranty_years?: number | null;
  // FE-214 — serial_number dropped on BE-202.
  brand?: string | null;
  notes?: string | null;
}

export interface ExtractMetadataResponse {
  extracted: ExtractedMetadata;
  transcript_echo: string;
  model_used: string;
}

export interface FileWithInventoryResponse {
  id: number;
  filename?: string;
  content_type: 'image' | 'text';
  file_type?: string;     // MIME type
  file_size?: number;     // Bytes
  storage_url?: string;   // GCS URL
  text_content?: string;  // For text content
  ai_description?: string;
  /** backend#109 — short AI label (1-3 words). Used as the scene
   *  card title when present, falls back to filename otherwise. */
  scene_label?: string | null;
  created_at: string;     // ISO datetime
  updated_at: string;     // ISO datetime
  inventory_items: InventoryItemResponse[]; // Associated items
}

export interface InventoryItemResponse {
  id?: number;
  item_name: string;
  item_description: string;
  item_category?: string;
  brand?: string;
  estimated_value?: number;
  /** How many of this item the user has. Integer ≥ 1. Persisted to
   *  the backend (verified via OpenAPI), with itemExtrasSlice kept
   *  as a fast client-side cache for legacy reads. */
  quantity?: number;
  condition?: string;
  location?: string;
  tags?: string;
  confidence_score: number;
  is_verified: boolean;

  // Sprint 3 / Chunk 10A — custody fields (BE-10.1 columns).
  // Optional + nullable: the backend exposes them on inventory list
  // responses once the user has any custody history. Browse cards +
  // ItemDetail use them to drive CustodyChip + CustodySection. The
  // owner_user_id flips into `user_id` for back-compat.
  user_id?: number | null;
  current_custodian_user_id?: number | null;
  custody_since?: string | null;
  custody_due_date?: string | null;

  // FE #198 / BE #286 — active-recall denormalization. Lets FE render
  // recall banners + row variants on the borrower's side without
  // having to fetch /users/borrow-requests separately for each item.
  //
  // - has_active_recall: true iff a borrow_requests row exists for
  //   this item with priority='RECALL' AND status='PENDING'.
  // - active_recall: full context blob (requester_name, reason,
  //   created_at, expected_by) — owner + custodian see it; other
  //   crew members get scrubbed per BE privacy rules.
  //
  // Both fields are OPTIONAL: BE rolls them out endpoint-by-endpoint,
  // and during the deploy window /users/inventory may return the
  // legacy shape. Treat `has_active_recall ?? false` as the source
  // of truth; missing == no recall.
  has_active_recall?: boolean;
  active_recall?: ActiveRecallInfo | null;

  // Sprint 2 / Chunk 9 — Gap A fields (backend#111, live on prod).
  // Crew bindings + GPS + reverse-geocoded address are now exposed
  // directly on /users/inventory responses. (FE-214 — `serial_number`
  // dropped per BE-202.)
  groups?: GroupSummaryForItem[];
  scan_lat?: number | null;
  scan_lng?: number | null;
  scan_address?: string | null;

  // Memory-Map Stage 2 — current location fields (backend#140).
  // Separate from scan_* (which stays frozen at original scan). User
  // can update these any time via LocationEditSheet. Map renders a
  // ghost pin at scan_* + solid pin at current_location_* + dashed
  // polyline connecting them when they differ.
  current_location_text?: string | null;
  current_location_lat?: number | null;
  current_location_lng?: number | null;
  current_location_address?: string | null;
  current_location_updated_at?: string | null;

  // Memory-Map Stage 2C — structured address (backend#143). The flat
  // current_location_address / scan_address strings are derived from
  // these fields server-side. FE populates them from Photon picks +
  // GPS button reverse-geocode results.
  current_location_street?: string | null;
  current_location_city?: string | null;
  current_location_state?: string | null;
  current_location_postcode?: string | null;
  current_location_country?: string | null;   // ISO-3166 alpha-2
  // Scan-time structured (write-once at scan-confirm; server-derived).
  scan_street?: string | null;
  scan_city?: string | null;
  scan_state?: string | null;
  scan_postcode?: string | null;
  scan_country?: string | null;

  // Sprint 8 / BE#160 — share-claim attribution.
  // When the row was created by claiming someone else's /share/{token}
  // (the legacy duplicate-row path, distinct from crew custody-flip):
  //   - claimed_from_user_id → original lender's user id
  //   - claimed_from_share_id → the ItemSharing row this row was claimed from
  //   - claimed_at → ISO timestamp of the claim
  // All three are null for rows the viewer scanned/created directly.
  // Drives the "Borrowed from {name}" pill on Browse cards + the
  // borrower-side context on ItemDetail (reliable signal — replaces
  // the previous item_name string-match heuristic).
  claimed_from_user_id?: number | null;
  claimed_from_share_id?: number | null;
  claimed_at?: string | null;

  // Sharing & Visibility Fields
  is_public?: boolean;
  is_shareable?: boolean;
  
  // Enhanced AI Analysis Fields
  perishability_type?: string;
  estimated_expiry_date?: string | null;
  expiry_confidence_score?: number;
  prominence_score?: number;
  visual_area_percentage?: number;
  ownership_likelihood?: number;
  exclusion_flags?: string[];
  requires_user_verification?: boolean;
  notes?: string;
  
  // Source Image Information
  source_file_id?: number;
  source_image_url?: string;
  source_url_expires_at?: string | null;

  // ──────────────────────────────────────────────────────────────────
  // Segmentation Pipeline Fields (feat/pipeline-v2 backend)
  // ──────────────────────────────────────────────────────────────────
  // Populated by the scan stream's `mask` SSE event and by
  // POST /inventory/{id}/resegment / POST /inventory/manual. Every
  // field is optional because:
  //   - Items from a fresh `item` SSE event arrive with bbox/mask null
  //     (backend fills them when the corresponding `mask` lands).
  //   - Legacy items in the Items grid predate segmentation entirely.
  //   - Primary-photo fields are null until the user confirms and the
  //     GET /inventory/{id}/primary-photo/stream resolves.
  // See docs/SEGMENTATION_PIPELINE_PRD.md (backend repo).

  /** Absolute pixel bbox in the NATURAL image coord system (same units
   *  as the `image_width_px` / `image_height_px` emitted by the
   *  `segmentation_started` event). */
  bbox?: ImageBbox | null;

  /** Signed GCS URL (PNG, alpha channel = object silhouette). Typically
   *  valid for ~24h; backend auto-refreshes on re-read. */
  mask_url?: string | null;

  /** SAM3's self-reported mask confidence (0–1). Distinct from
   *  `confidence_score` which is Vision's item-detection confidence. */
  segmentation_confidence?: number | null;

  /** Which pipeline produced the mask.
   *    'sam3'              — SAM3 ran and picked the best candidate.
   *    'user_box'          — user-drawn rectangle, no SAM3.
   *    'user_box_fallback' — SAM3 returned nothing, we fell back to
   *                          a rectangular mask from the user's bbox. */
  segmentation_model?: 'sam3' | 'user_box' | 'user_box_fallback' | null;

  /** The transparent-background PNG crop generated by the primary-photo
   *  stream. Render on a checkerboard backdrop in the Done view. */
  primary_photo_url?: string | null;

  /**    'crop_upscale' — happy path (crop + AuraSR 4× + mask re-apply).
   *    'crop_only'    — upscale failed; raw crop is fine.
   *    'original'     — mask missing entirely; fell back to room photo. */
  primary_photo_source?: 'crop_upscale' | 'crop_only' | 'original' | null;

  // ──────────────────────────────────────────────────────────────────
  // FE-212 / BE-201 + BE-208 — multi-photo carousel
  // ──────────────────────────────────────────────────────────────────
  /** Ordered list of every active photo on this item. Position 0 is the
   *  PRIMARY_AUTO row (the AuraSR cutout from the scan); positions 1+
   *  are user-added (USER_UPLOAD) or rescanned (ADDITIONAL_SCAN) photos.
   *  Empty array for legacy items pre-BE-201 backfill — caller should
   *  fall back to `primary_photo_url` for the hero. Max 4 user-added
   *  photos enforced by backend (5 total). */
  photos?: PhotoSummary[];
}

/** FE-212 — source of a photo in the multi-photo array.
 *
 *   - 'PRIMARY_AUTO'    — auto-generated by the scan pipeline (AuraSR
 *                          cutout). Always position 0. Invariant —
 *                          cannot be reordered or deleted by the user.
 *   - 'USER_UPLOAD'     — manually added via ItemDetail's Manage photos
 *                          surface. Reorderable + deletable.
 *   - 'ADDITIONAL_SCAN' — fresh photo from a re-scan flow. Same
 *                          permissions as USER_UPLOAD. */
export type PhotoSource = 'PRIMARY_AUTO' | 'USER_UPLOAD' | 'ADDITIONAL_SCAN';

/** Single row in `InventoryItemResponse.photos[]` and the return value
 *  of `GET /inventory/{id}/photos`. The signed `url` is auto-refreshed
 *  server-side when within an hour of expiry. */
export interface PhotoSummary {
  id: number;
  url: string;
  display_order: number;
  source: PhotoSource;
  content_type?: string | null;
  file_size_bytes?: number | null;
  exif_taken_at?: string | null;
  exif_gps_lat?: number | null;
  exif_gps_lng?: number | null;
  exif_camera?: string | null;
  uploaded_at: string;
}

/** Request body for `PATCH /inventory/{item_id}/photos/reorder`.
 *
 *  `photo_ids` is the FULL set of user-added (display_order >= 1)
 *  photos for this item, in the desired new order. Position 0
 *  (PRIMARY_AUTO) is invariant and NOT included. Backend rejects
 *  any list that doesn't exactly match the caller's user-added set. */
export interface PhotoReorderRequest {
  photo_ids: number[];
}

/** FE-212 — client-side cap, mirrors backend `MAX_USER_PHOTOS = 4`.
 *  Exported so screens can disable the [+ add] button at the limit. */
export const MAX_USER_PHOTOS = 4;

/** Phase 3F — single row in the location-change history for an item.
 *  Returned by `GET /inventory/{item_id}/location-history` per
 *  backend#144. Privacy guard: non-owner crew members see only
 *  structured fields + flat address; lat/lng nulled out for them. */
export interface ItemLocationEvent {
  id: number;
  actor_user_id: number | null;
  actor_name: string | null;
  /** Backend writes one of:
   *  - 'SCAN' — initial scan-confirm
   *  - 'MANUAL_EDIT' — generic PATCH from anywhere
   *  - 'GPS_BUTTON' — captureScanLocation via LocationEditSheet
   *  - 'ADDRESS_PICKER' — Photon suggestion picked
   *  - 'REVERSE_GEOCODE' — async server-side fill of structured fields
   *  The FE accepts any string (forward-compat for future sources). */
  source: string;
  text: string | null;
  lat: number | null;
  lng: number | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  address: string | null;
  note: string | null;
  created_at: string;
}

export interface LocationHistoryResponse {
  events: ItemLocationEvent[];
  total: number;
}

/** Phase 3H — single chip suggestion from `GET /users/locations/top`
 *  per backend#145. */
export interface TopLocationItem {
  /** Display label preserving the user's most-recently-saved casing. */
  label: string;
  /** Lowercased + trimmed form, used for client-side dedupe/match. */
  normalized_label: string;
  /** How many of the user's items currently use this label. */
  item_count: number;
}

export interface TopLocationsResponse {
  locations: TopLocationItem[];
}

/** Absolute pixel bbox in natural image coordinate space. Fields are
 *  integers (backend rejects fractional floats). */
export interface ImageBbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface UpdateInventoryItemRequest {
  item_name?: string;
  item_description?: string;
  item_category?: string;
  brand?: string;
  estimated_value?: number;
  condition?: string;
  location?: string;
  tags?: string;
  notes?: string;
  /**
   * User-verified state. The backend accepts this field today when
   * patching an item (Pydantic models pass it through via the
   * generic update path); if it ever stops accepting we fall back
   * to a client-side optimistic merge so the Verify flow still
   * feels responsive.
   */
  is_verified?: boolean;
  /**
   * Quantity (integer ≥1). Backend exposes this on the update path
   * (verified via OpenAPI). Previously tracked client-side via
   * itemExtrasSlice for instant UX; now we also persist server-side
   * so the value survives device wipes + cross-device installs.
   */
  quantity?: number;
  /**
   * Memory-Map Stage 2 — current location fields (backend#140).
   * Distinct from `scan_*` which is write-once at scan time. These
   * three move freely as the user re-locates the item. Sending lat
   * + lng together triggers server-side reverse-geocoding into
   * `current_location_address`. Send null to clear.
   */
  current_location_text?: string | null;
  current_location_lat?: number | null;
  current_location_lng?: number | null;
  /**
   * Memory-Map Stage 2C — structured address (backend#143). When the
   * user picks a Photon suggestion OR taps the GPS button (which
   * reverse-geocodes), FE extracts these fields and sends them. The
   * backend's FE-precedence rule means user-typed values are not
   * overwritten by the async reverse-geocode worker; it only fills
   * NULL fields.
   */
  current_location_street?: string | null;
  current_location_city?: string | null;
  current_location_state?: string | null;
  current_location_postcode?: string | null;
  current_location_country?: string | null;   // ISO-3166 alpha-2
}

export interface UpdateInventoryItemResponse extends InventoryItemResponse {
  // The API returns the updated item directly, not wrapped in inventory_item
}

export interface FilesWithInventoryResponse {
  files: FileWithInventoryResponse[];
  pagination: PaginationInfo;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  total_pages?: number;
  current_page?: number;
  has_next: boolean;
  has_previous: boolean;
}

// Signed URL Refresh Types
export interface RefreshUrlsRequest {
  file_ids: number[];
}

export interface RefreshedUrl {
  file_id: number;
  signed_url: string | null;
  expires_at: string | null;
  success: boolean;
  error?: string;
}

export interface RefreshUrlsResponse {
  success: boolean;
  refreshed_urls: RefreshedUrl[];
  message: string;
}

// Individual Detail Endpoints Types
export interface FileDetailsResponse {
  id: number;
  filename?: string;
  content_type: 'image' | 'text';
  file_type?: string;              // MIME type (e.g., "image/jpeg")
  file_size?: number;              // Size in bytes
  storage_url?: string;            // Fresh signed URL (24hr expiry)
  url_expires_at?: string;         // ISO timestamp of URL expiry
  text_content?: string;           // For text-type content
  ai_description?: string;         // AI-generated description
  created_at: string;              // ISO timestamp
  updated_at: string;              // ISO timestamp
  inventory_items: InventoryItemResponse[]; // Associated items from this file
}

export interface InventoryItemDetailsResponse {
  id: number;
  item_name: string;
  item_description: string;
  item_category?: string;
  brand?: string;
  estimated_value?: number;
  condition?: string;              // "new", "used", "damaged", "unknown"
  location?: string;
  tags?: string;                   // Comma-separated tags
  confidence_score: number;        // AI confidence (0.0 - 1.0)
  is_verified: boolean;            // User-verified status
}

// Delete Operation Response Types
export interface VectorDeletionResults {
  success: boolean;
  partial_success: boolean;
  deleted_collections: string[];
  failed_collections: string[];
  total_collections: number;
  file_id: number;
  user_id: number;
}

export interface DeleteFileResponse {
  success: boolean;
  message: string;
  file_id: number;
  deleted_items_count: number;
  vector_deletion_results: VectorDeletionResults;
}

export interface DeleteInventoryItemResponse {
  success: boolean;
  message: string;
  item_id: number;
  vector_deletion_results: VectorDeletionResults;
}

// ========================================================================
// Workspaces / Groups (Chunk 8 — Sprint 1)
// ------------------------------------------------------------------------
// Mirrors the Pydantic schema on the backend. Field names are 1:1 — never
// camelCase the wire format. Numeric IDs (NOT string UUIDs); the legacy
// AsyncStorage mock used uuid-strings, but the real backend uses ints.
//
// Reference: BE-8.1 migration (group_workspace + group_member tables).
// ========================================================================

// BE-200 — group kind enum migration shipped: CREW → TEAM (renamed),
// old TEAM → FRIENDS (renamed), NEIGHBOURS added. PERSONAL unchanged.
// 'CREW' kept as a legacy alias so cached responses (or anything
// pre-migration) don't crash the FE. FE-202 will produce a polished
// kind picker; this type only needs to enumerate the wire values.
export type GroupKind =
  | 'TEAM'
  | 'FAMILY'
  | 'FRIENDS'
  | 'NEIGHBOURS'
  | 'PERSONAL'
  | 'CREW'; // legacy
export type GroupMemberRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';
export type GroupMemberStatus = 'INVITED' | 'ACTIVE' | 'LEFT' | 'REMOVED';

/** Member row shape returned by the backend. Sprint 1 reality: backend
 *  doesn't yet enrich with user.name / user.email / user.avatar_url
 *  (filed as remembr-xyz/backend#103). All three are optional + nullable
 *  so the frontend can render a placeholder and fold in the join when
 *  it lands. */
export interface GroupMember {
  /** Membership row PK (NOT the user_id). */
  id?: number;
  /** Null while invite is INVITED with no account yet. */
  user_id: number | null;
  /** Joined when backend lands user-table enrichment. Null in Sprint 1. */
  name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  /** Pending-invite email — populated when user_id is null. */
  invitee_email?: string | null;
  invited_by?: number | null;
  role: GroupMemberRole;
  status: GroupMemberStatus;
  joined_at: string | null;
  created_at?: string;
}

/** Base group shape returned by `POST /groups` (no members) AND `PATCH
 *  /groups/{id}` (no members). Mirrors the bare `group_workspace` row. */
export interface GroupBase {
  id: number;
  name: string;
  kind: GroupKind;
  owner_user_id: number;
  description?: string | null;
  auto_approve_handoff: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Tile shape returned by `GET /groups` (list). Sprint 1: backend
 *  returns the bare `GroupBase` shape — `my_role`, `member_count`,
 *  `item_count` are NOT yet populated server-side (filed as
 *  remembr-xyz/backend#103). Frontend marks them optional + computes
 *  what it can client-side (`my_role` from `owner_user_id` comparison;
 *  counts default to 0 until detail-fetch). */
export interface GroupListItem extends GroupBase {
  my_role?: GroupMemberRole;
  member_count?: number;
  item_count?: number;
}

/** Full group shape returned by `GET /groups/{id}` (detail). Includes
 *  the members[] array. `my_role` may be absent (computed client-side)
 *  until backend#103 lands. */
export interface GroupDetail extends GroupBase {
  members: GroupMember[];
  my_role?: GroupMemberRole;
}

/** POST /groups create request */
export interface CreateGroupRequest {
  name: string;
  kind: GroupKind;
  description?: string;
}

/** PATCH /groups/{id} — partial update */
export interface PatchGroupRequest {
  name?: string;
  description?: string;
  auto_approve_handoff?: boolean;
  archived?: boolean;
}

/** POST /groups/{id}/members invite request — Sprint 1 is email-only.
 *  The phone field is reserved for Chunk 14 (SMS invites); v1 backend
 *  returns 400 if phone is sent without email. */
export interface InviteMemberRequest {
  invitee_email: string;
  role?: GroupMemberRole;
  note?: string;
}

/** POST /groups/{id}/members response — both 201 (new) and 200 (re-invite).
 *  Backend bonus: `invite_url` ready-built for the frontend to surface
 *  in the success toast / share-with-recipient flow. */
export interface InviteMemberResponse {
  member_id: number;
  invite_token: string;
  invite_url?: string;            // bonus — backend builds the deep link for us
  expires_at: string;             // ISO 14d future
}

/** POST /groups/accept-invite request */
export interface AcceptInviteRequest {
  invite_token: string;
}

/** POST /groups/accept-invite response. Sprint 1: backend may return
 *  the membership record alongside the group; both shapes accepted. */
export interface AcceptInviteResponse {
  group: GroupDetail;
  membership?: {
    user_id: number;
    role: GroupMemberRole;
    status: GroupMemberStatus;
    joined_at: string;
  };
}

/** Single row returned by `GET /groups/invites/received`. backend#114
 *  (live on prod). Pre-joins inviter + group display fields so the
 *  CrewHub banner renders without extra lookups. */
export interface ReceivedInviteOut {
  invite_token: string;
  group_id: number;
  group_name: string;
  group_kind: GroupKind | string;
  inviter_user_id?: number | null;
  inviter_name?: string | null;
  role: GroupMemberRole | string;
  note?: string | null;
  created_at: string;
  expires_at?: string | null;
}

/** Envelope returned by `GET /groups/invites/received`. */
export interface ReceivedInvitesResponse {
  invites: ReceivedInviteOut[];
}

/** PATCH /groups/{id}/members/{user_id} role change */
export interface PatchMemberRoleRequest {
  role: GroupMemberRole;
}

/** GET /groups response. Sprint 1: backend returns a bare array (no
 *  envelope). The TS type is just the array; the api method exposes
 *  `{ groups: [...] }` to keep call sites readable + future-proof
 *  against backend#103 wrapping the response. */
export type ListGroupsResponse = GroupListItem[];

/* ─── Sprint 2 (Chunk 9) — auto-suggest + group rules + group inventory ── */

/** Rule types the backend understands today. Future-proof open enum
 *  so a new server-side rule type doesn't break the client. */
export type GroupRuleType =
  | 'LOCATION_RADIUS'
  | 'TAG_PREFIX'
  | 'SOURCE_DEVICE'
  | (string & {});

/** POST /groups/{id}/rules — body. */
export interface CreateGroupRuleRequest {
  rule_type: GroupRuleType;
  /** JSON-encoded rule payload, e.g. `JSON.stringify({lat,lng,radius_m})`
   *  for LOCATION_RADIUS. */
  rule_value: string;
  priority?: number;
}

/** GET / POST /groups/{id}/rules row. ⚠️ No PATCH endpoint exists —
 *  to "edit" a rule, DELETE then POST a fresh one. */
export interface GroupRule {
  id: number;
  group_id: number;
  rule_type: GroupRuleType;
  rule_value: string;
  priority: number;
  created_at?: string;
  updated_at?: string;
}

/** POST /groups/{id}/items — body. */
export interface AddItemsToGroupRequest {
  item_ids: number[];
}

/** POST /groups/{id}/items — response. `skipped` lists items the
 *  caller doesn't own (silently dropped per BE-9.2's authz pattern). */
export interface AddItemsToGroupResponse {
  group_id: number;
  added: number;
  already_bound: number;
  skipped: number[];
}

/** GET /groups/{id}/inventory — single row. Pulls Sprint 2's
 *  scan_lat/scan_lng through. (FE-214 — `serial_number` dropped on
 *  BE-202.) */
export interface GroupItemSummary {
  item_id: number;
  /** Owner user_id (per BE response — synonymous with item.user_id). */
  user_id?: number;
  item_name?: string;
  item_description?: string;
  item_category?: string;
  brand?: string;
  estimated_value?: number;
  quantity?: number;
  unit?: string;
  primary_photo_url?: string;
  primary_photo_url_expires_at?: string;
  scan_lat?: number | null;
  scan_lng?: number | null;
  /** Internal `item_group` row id — useful for DELETE /items/{item_id}
   *  (the URL still uses item_id, but having binding_id around is
   *  helpful for optimistic-remove undo flows). */
  binding_id: number;
  added_by?: number;
  added_at: string;

  // ── FE-220 / SG-1 + SG-2 (rev-3 batch — backend rev 00238-5ct) ────
  /** SG-1: per-row availability hint surfaced by `GroupItemSummary`.
   *
   *   - 'available'         — owner holds, no active share, free to borrow
   *   - 'in_use_by_member'  — held by an in-group member (custodian id visible per SG-2)
   *   - 'lent_out'          — held by someone outside the group; identity hidden
   *   - 'shared'            — under an active 1-on-1 share (PENDING/CLAIMED)
   *   - 'recalled'          — owner has issued an active /recall
   *
   * Open-ended union because BE may add new states (e.g., 'overdue')
   * without breaking the client. */
  availability_state?:
    | 'available'
    | 'in_use_by_member'
    | 'lent_out'
    | 'shared'
    | 'recalled'
    | string;
  /** Convenience derived flag from the server — TRUE for everything that
   *  isn't 'available'. Lets the FE skip enum-matching for binary chips. */
  is_currently_out?: boolean;
  /** SG-2: present only when the custodian is also an ACTIVE member of
   *  this group OR the viewer is the item owner. Stripped to null
   *  otherwise — never leak custodian identity across group boundaries. */
  current_custodian_user_id?: number | null;
  /** Custody-since timestamp (ISO). Null pre-handoff. */
  custody_since?: string | null;
  /** Optional return-by date set at handoff time. */
  custody_due_date?: string | null;
  /** Owner display name — denormalized server-side so the FE doesn't
   *  have to resolve user_id → name across cached group rosters. */
  owner_name?: string | null;
  /** Address (city-level) — exposed to all viewers; raw scan_lat/lng
   *  remain null-stripped for non-owners per #112. */
  scan_address?: string | null;

  // FE #198 / BE #286 — active-recall denormalization.
  // GroupItemSummary only exposes the boolean flag (full
  // active_recall object is on the detail endpoint). Optional during
  // BE rollout; treat `has_active_recall ?? false` as the source of
  // truth. Drives the red row variant when viewer is the custodian.
  has_active_recall?: boolean;
}

/** GET /groups/{id}/inventory — paginated envelope. */
export interface GroupInventoryResponse {
  group_id: number;
  total: number;
  limit: number;
  offset: number;
  items: GroupItemSummary[];
}

/* ─── Sprint 3 (Chunk 10A) — Custody flow ──────────────────────────── */

/** Action types on `item_custody_event`. Open enum so a new
 *  server-side action class doesn't break the client. */
export type CustodyAction =
  | 'HANDOFF'
  | 'RETURN'
  | 'RECONCILE'
  | 'RESET'
  | 'LEND_OUT'
  | 'LEND_BACK'
  | 'RECALL'
  | 'REMIND'
  | (string & {});

/** POST /inventory/{id}/handoff — owner-push body. Custody flips
 *  immediately, no approval needed. */
export interface HandoffRequest {
  recipient_user_id: number;
  group_id?: number | null;
  due_date?: string | null; // ISO datetime
  note?: string | null;
  condition_photo_url?: string | null;
}

/** POST /inventory/{id}/handoff-request — member-pull body. Server
 *  may auto-approve (group's auto_approve_handoff = true) or queue
 *  pending owner approval. */
export interface HandoffRequestRequest {
  group_id: number;
  expected_return_date?: string | null;
  message?: string | null;
}

/** POST /inventory/{id}/handoff-request — response. */
export interface HandoffRequestResponse {
  item_id: number;
  auto_approved: boolean;
  request_id?: number | null;
  current_custodian_user_id: number;
  event?: CustodyEventSummary | null;
}

/** POST /inventory/{id}/return — body. */
export interface ReturnRequest {
  note?: string | null;
  condition_photo_url?: string | null;
}

/** Single row in the custody history timeline. */
export interface CustodyEventSummary {
  id: number;
  action: CustodyAction;
  from_user_id?: number | null;
  to_user_id?: number | null;
  actor_user_id: number;
  group_id?: number | null;
  due_date?: string | null;
  note?: string | null;
  condition_photo_url?: string | null;
  created_at: string;
}

/** GET /search/resolve — single result row. Cross-group resolver.
 *  Pre-joined with owner/custodian names so the FE doesn't need to
 *  hit /users/{id} for each row. `group_ids` lists only crews the
 *  caller is also in. Sprint 4 / Chunk 10B FE-10.7. */
export interface ResolvedItem {
  item_id: number;
  item_name?: string | null;
  item_description?: string | null;
  item_category?: string | null;
  primary_photo_url?: string | null;
  owner_user_id: number;
  owner_name?: string | null;
  current_custodian_user_id?: number | null;
  custodian_name?: string | null;
  custody_due_date?: string | null;
  custody_since?: string | null;
  group_ids: number[];
}

/** GET /search/resolve — paginated envelope. */
export interface ResolveResponse {
  query: string;
  total: number;
  limit: number;
  offset: number;
  items: ResolvedItem[];
}

/** POST /inventory/{id}/recall — body. Owner forces a return on an
 *  item out with someone else. Backend creates a high-priority
 *  borrow_requests row (`priority='RECALL'`) and emails the holder. */
export interface RecallRequest {
  /** Free-text reason shown in the email + recorded on the
   *  BorrowRequest. Helps the holder triage. Max 2000 chars. */
  reason?: string | null;
}

/** POST /inventory/{id}/recall — response. */
export interface RecallResponse {
  item_id: number;
  request_id: number;
  holder_user_id: number;
  notified: boolean;
}

/** GET /groups/{id}/activity — single row, denormalized.
 *
 *  Sourced from `item_custody_event` rows whose `group_id` matches
 *  the queried group. The backend pre-joins user + item rows so each
 *  event ships with display-ready names — no client-side member-name
 *  lookup needed (unlike CustodyEventSummary which is item-scoped). */
export interface GroupActivityEvent {
  id: number;
  action: CustodyAction;
  item_id: number;
  item_name?: string | null;
  from_user_id?: number | null;
  from_user_name?: string | null;
  to_user_id?: number | null;
  to_user_name?: string | null;
  actor_user_id: number;
  actor_user_name?: string | null;
  note?: string | null;
  due_date?: string | null;
  created_at: string;
}

/** GET /groups/{id}/activity — paginated envelope. */
export interface GroupActivityResponse {
  group_id: number;
  total: number;
  limit: number;
  offset: number;
  events: GroupActivityEvent[];
}

// ========================================================================
// SPRINT 5 / CHUNK 11 — Packs (event-scoped item subsets)
// Reference: BE-11.1 migration + BE-11.2/3/4/5 endpoints (all live).
// Field names match the live OpenAPI spec on backend.remembr.xyz.
// ========================================================================

/** Pack lifecycle states. The state machine is:
 *    DRAFT → PREPARED → IN_PROGRESS → WRAPPED → ARCHIVED
 *  Backward transitions allowed EXCEPT WRAPPED → IN_PROGRESS
 *  (create a new pack instead per the BE-11.4 spec). */
export type PackStatus =
  | 'DRAFT'
  | 'PREPARED'
  | 'IN_PROGRESS'
  | 'WRAPPED'
  | 'ARCHIVED'
  | (string & {});

/** Tile row returned in the `GET /groups/{id}/packs` envelope.
 *  Matches the live `PackResponse` schema. */
export interface PackSummary {
  id: number;
  group_id: number;
  name: string;
  description?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  status: PackStatus;
  created_by: number;
  item_count: number;
  created_at: string;
  updated_at: string;
}

/** Single pack_item row. Matches the live `PackItemSummary` schema. */
export interface PackItem {
  /** pack_item row id (NOT the underlying inventory item id). */
  id: number;
  item_id: number;
  item_name?: string | null;
  added_by?: number | null;
  added_at: string;
  /** True once a wrap scan matched the item (Chunk 12 reconciliation). */
  present_at_wrap?: boolean | null;
  reconcile_score?: number | null;
}

/** Full pack returned by `GET /packs/{id}`. Matches the live
 *  `PackDetailResponse` schema (extends PackResponse + items[]). */
export interface PackDetail extends PackSummary {
  items: PackItem[];
}

/** Body for `POST /groups/{id}/packs`. Items are NOT included on
 *  create per the live spec — call `addPackItems` after create. */
export interface CreatePackRequest {
  name: string;
  description?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
}

/** Body for `PATCH /packs/{id}`. */
export interface PatchPackRequest {
  name?: string | null;
  description?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  status?: PackStatus | null;
}

/** Body for `POST /packs/{id}/items`. */
export interface AddPackItemsRequest {
  item_ids: number[];
}

/** Response for `POST /packs/{id}/items`. Backend richly categorizes
 *  skipped ids into "already bound to this pack" vs "not in pack's
 *  group" — the FE surfaces the second as an inline warning. */
export interface AddPackItemsResponse {
  added: PackItem[];
  skipped_already_bound: number[];
  skipped_not_in_group: number[];
}

/** Envelope for `GET /groups/{id}/packs`. Backend response is just
 *  `{ packs: PackResponse[] }` — no pagination cursors today. */
export interface PackListResponse {
  packs: PackSummary[];
}

/** Response for `POST /packs/{id}/wrap`. Chunk 12 reconciliation
 *  flow — when an owner wraps a pack, the server auto-returns any
 *  items still with the owner (no custody flip needed) and reports
 *  the count of items still out with non-owners. */
export interface WrapPackResponse {
  pack_id: number;
  status: PackStatus;
  auto_returned_count: number;
  still_with_others_count: number;
  auto_returned_item_ids: number[];
}

/** GET /inventory/{id}/custody — paginated envelope. */
export interface CustodyHistoryResponse {
  item_id: number;
  current_custodian_user_id?: number | null;
  custody_since?: string | null;
  custody_due_date?: string | null;
  total: number;
  events: CustodyEventSummary[];
}

/** Returned by handoff + return endpoints — confirms the flip + the
 *  written audit row in one shot. */
export interface CustodyFlipResponse {
  item_id: number;
  current_custodian_user_id: number;
  custody_since: string;
  custody_due_date?: string | null;
  event: CustodyEventSummary;
}

// ────────────────────────────────────────────────────────────────────
// Sprint 8 — Notifications (backend#151)
// ────────────────────────────────────────────────────────────────────

/** All 13 notification types fanned out by the server. FE accepts any
 *  string for forward-compat (new types added server-side render with
 *  the generic copy + bell icon). */
/** Server-shipped notification type union.
 *
 *   Sprint-8 wave (BE rev 00238+) renamed:
 *     CREW_INVITE_*  → GROUP_INVITE_*   (SG-18 migration)
 *
 *   And added new types:
 *     GROUP_INVITE_DECLINED      — invitee declines (BE-212)
 *     CUSTODY_REMINDER           — owner nudge to custodian (SG-16)
 *     SHARE_CANCELLED_BY_OWNER   — share auto-cancelled by borrow-approval (SG-22 R2)
 *
 *  Old CREW_INVITE_* names stay in the union as a defensive fallback so
 *  the FE survives a race where the server still emits a legacy name
 *  during the migration cutover. The notification-card renderer treats
 *  the old + new names as synonyms.
 *
 *  Open-ended `| string` lets brand-new server types render with a
 *  generic card instead of crashing.
 */
export type NotificationType =
  | 'SHARE_RECEIVED'
  | 'SHARE_CLAIMED'
  | 'SHARE_CANCELLED_BY_OWNER'       // SG-22 R2 — borrow-approval auto-cancels share
  | 'GROUP_INVITE_RECEIVED'          // renamed from CREW_INVITE_RECEIVED
  | 'GROUP_INVITE_ACCEPTED'          // renamed from CREW_INVITE_ACCEPTED
  | 'GROUP_INVITE_DECLINED'          // NEW (BE-212)
  | 'CREW_INVITE_RECEIVED'           // legacy — defensive
  | 'CREW_INVITE_ACCEPTED'           // legacy — defensive
  | 'CREW_INVITE_DECLINED'           // legacy — defensive
  | 'BORROW_REQUEST_RECEIVED'
  | 'BORROW_APPROVED'
  | 'BORROW_DECLINED'
  | 'CUSTODY_HANDOFF'
  | 'CUSTODY_RETURNED'
  | 'CUSTODY_RECALL'
  | 'CUSTODY_RECALL_ACK'
  | 'CUSTODY_REMINDER'               // NEW (SG-16)
  | 'OVERDUE'
  | 'WAITLIST_UPDATE'
  | string;

/** Priority tier — drives badge color + push behavior on native. */
export type NotificationPriority = 'NORMAL' | 'URGENT';

/** Optional polymorphic target the notification points at. The FE
 *  uses {type,id} to deep-link from the card → ItemDetail /
 *  GroupDetail / SharingRequests. */
export type NotificationTargetType =
  | 'item'
  | 'group'
  | 'share'
  | 'borrow_request'
  | 'invite'
  | 'pack'
  | string;

/** A single row in the user's notifications inbox.
 *  Schema mirrors backend#151 `notifications` table. */
export interface NotificationItem {
  id: number;
  type: NotificationType;

  // Who caused this notification. Both nullable for system-generated
  // events (cron-based OVERDUE pings, REVERSE_GEOCODE fan-outs etc.)
  actor_user_id: number | null;
  actor_name: string | null;
  actor_avatar_url?: string | null;

  // What this notification points at. `target_type` + `target_id`
  // resolve to a route the FE can navigate to. Optional payload
  // carries extra context the card may render (group name, item
  // name, due date) without the FE needing a second fetch.
  target_type: NotificationTargetType | null;
  target_id: number | null;
  payload: Record<string, unknown> | null;

  // Human-facing copy. Server pre-renders both so FE doesn't have
  // to localize 13 distinct templates today.
  title: string;
  body: string | null;

  // Optional deep-link path (e.g. `/share/{token}` or
  // `/groups/{id}`). FE prefers explicit navigation built off
  // target_type/target_id, but falls back to action_url when
  // unfamiliar with the type (forward-compat).
  action_url?: string | null;

  priority: NotificationPriority;

  /** ISO-8601 timestamp when this row was created. */
  created_at: string;

  /** ISO-8601 timestamp when the recipient marked the row read;
   *  null while unread. */
  read_at: string | null;

  /** ISO-8601 timestamp when the recipient dismissed; null
   *  otherwise. Dismissed rows are filtered server-side from
   *  default list responses. */
  dismissed_at: string | null;
}

/** `GET /users/notifications` envelope (BE-151 final spec).
 *  Server does NOT echo `limit`/`offset`/`has_more` — the FE computes
 *  `has_more = (offset + notifications.length) < total` locally. */
export interface NotificationListResponse {
  notifications: NotificationItem[];
  total: number;
  unread_count: number;
}

/** `GET /users/notifications/unread-count` (BE-151) — cheap polled
 *  endpoint. Server returns `{ count }` (NOT `{ unread_count }`). */
export interface UnreadCountResponse {
  count: number;
}

/** `POST /users/notifications/{id}/read` (BE-151) returns 204.
 *  FE updates its cached state locally — the helper synthesizes a
 *  small envelope so the slice has a stable contract. */
export interface MarkReadResult {
  notification_id: number;
  read_at: string;
}

/** `POST /users/notifications/mark-all-read` (BE-151) returns
 *  `{ updated: N }`. Accepts an optional `before` ISO-8601 cutoff
 *  so the FE can preserve real-time unread events that landed
 *  during screen render. */
export interface MarkAllReadResponse {
  updated: number;
}

/** Query params the FE may pass when listing notifications.
 *  Server supports only these three (BE-151). `limit` is capped
 *  server-side at 100. */
export interface NotificationListParams {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
}

/* ════════════════════════════════════════════════════════════════
 * Scan History (FE #124 / backend#312 + #313)
 *
 * Lightweight per-scan summary returned by `GET /users/scans`. The
 * full nested `inventory_items` array is fetched separately via
 * `GET /users/scans/{file_record_id}` when the FE needs to resume
 * an unfinished scan into SegmentScreen.
 *
 * See USJ.md J2.B4 v2 for the wireframes + state machine.
 * ════════════════════════════════════════════════════════════════ */

/** Scan lifecycle states. The BE owns the transitions; the FE only
 *  reads them to render the status pill + decide which CTA to show
 *  per row. */
export type ScanStatus =
  | 'IN_PROGRESS' // SSE stream is open, items still arriving
  | 'READY_TO_SAVE' // Stream complete, user hasn't tapped Save yet
  | 'COMPLETED' // confirm/batch fired, items live in userinventory
  | 'FAILED' // Pipeline error / SSE drop / confirm/batch 5xx
  | 'CANCELLED'; // User discarded via DELETE /users/scans/{id}

/** Per-row summary in `GET /users/scans` list response. Matches
 *  backend#312 acceptance criteria. */
export interface ScanHistoryItem {
  file_record_id: number;
  filename: string;
  /** Signed thumbnail URL (7-day expiry per BE-side cache). May be
   *  null for very-old scans without a derived thumbnail. */
  thumbnail_url: string | null;
  /** Raw enum slug from the AI room-tagging pass, e.g. "DINING_ROOM".
   *  FE formats for display via `formatRoomLabel()` from
   *  SegmentScreen.tsx (the same helper that title-cases the
   *  SegmentScreen header chip — Gap #121). */
  room_context: string | null;
  /** Scan lifecycle state. */
  status: ScanStatus;
  /** COUNT of inventory_items where file_record_id=X AND saved_at IS NULL.
   *  Drives the row subtitle ("12 items detected"). */
  detected_count: number;
  /** COUNT where saved_at IS NOT NULL. Drives COMPLETED row subtitle
   *  ("11 items saved"). */
  saved_count: number;
  /** Timestamp of the last status transition. Drives the relative-
   *  time label ("Pending 12 min ago"). NOT created_at — a scan that
   *  finished detecting 10 min ago and sat at READY_TO_SAVE for 2 more
   *  should read "Pending 2 min ago", which means the FE renders the
   *  delta from last_event_at. */
  last_event_at: string;
  /** Populated when status='FAILED'. Examples per backend#313:
   *    'pipeline_5xx'      — AI model errored mid-stream
   *    'sse_drop'          — last event > 30s ago, server timeout
   *    'ai_quota_exceeded' — OpenAI / Gemini rate-limited */
  error_code: string | null;
  created_at: string;
}

/** Response shape for `GET /users/scans`. Pagination matches the
 *  same shape as EnhancedInventoryResponse. */
export interface ScanHistoryResponse {
  scans: ScanHistoryItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    total_pages: number;
    current_page: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

/** Query params for `GET /users/scans`. */
export interface ScanHistoryListParams {
  limit?: number; // default 20, cap 100
  offset?: number;
  sort_by?: 'created_at' | 'last_event_at';
  sort_order?: 'asc' | 'desc';
  /** Optional filter — e.g. `status=IN_PROGRESS&limit=1` is the cheap
   *  Dashboard banner query ("does the user have any live scans?"). */
  status?: ScanStatus;
}

/** Full scan detail returned by `GET /users/scans/{file_record_id}`.
 *  Includes the nested inventory_items so the FE can resume the scan
 *  in SegmentScreen with detected items already laid out on the photo. */
export interface ScanDetailResponse extends ScanHistoryItem {
  /** Pre-confirm detected items. Each has bbox geometry + raw AI
   *  output (name / category / value). FE uses these to seed the
   *  SegmentScreen viewer when status === IN_PROGRESS or READY_TO_SAVE.
   *  When status === COMPLETED, this is empty (items live in
   *  /users/inventory now). */
  inventory_items: InventoryItemResponse[];
  /** File record metadata — original photo URL (separate from
   *  thumbnail_url which is a small derived crop). */
  source_image_url: string | null;
}